import React, { useEffect, useState } from 'react';
import Modal from '../../components/Shell/Modal';
import useModal from '../../hooks/useModal';
import {
	CheckCircleIcon,
	ChevronRightIcon,
	ClockIcon,
	DocumentCheckIcon,
	EllipsisVerticalIcon,
	MinusCircleIcon,
	PlusCircleIcon,
	PlusIcon,
	WrenchScrewdriverIcon,
	XCircleIcon,
	XMarkIcon,
} from '@heroicons/react/24/outline';
import {
	TemplateBoolean,
	TemplateCheckbox,
	TemplateContact,
	TemplateCreateable,
	TemplateDate,
	TemplateDropdown,
	TemplateField,
	TemplateReactSelect,
	TemplateTeamMembers,
} from '../../components/Attributes/Attributes_old';
import FileUpload from '../../components/nextgen/FileUpload';
import usePrevious from '../../hooks/usePrevious';
import useAsync from '../../hooks/useAsync';
import {
	getModuleCategories,
	getModuleFieldsBeta,
	updateModuleFieldActive,
} from '../../api/modules';
import Button from '../../components/Button';
import Attributes, { configurePayload } from '../../components/Attributes';
import LoadingSkeleton from '../../components/Shell/LoadingSkeleton';
import { FIELD_OPTIONS } from '../../components/helpers/constants';
import { capitalize } from 'lodash';
import {
	capitalizeFirstLetter,
	classNames,
	getUserFullName,
} from '../../components/helpers/functions';
import { useRequiredBeta } from '../../hooks/useRequired';
import PDFModal from '../PDFModal';
import { addContract } from '../../api';
import moment from 'moment';
import { NOTIFY_OPTIONS } from '../../constants';
import { AvatarGenerated } from '../../components/Avatar';
import { getTeam } from '../../api/company';
import ContractPMSchedules from './ContractPMSchedules';
import { Menu } from '@headlessui/react';
import GeneralStepContract from './steps/GeneralStepContract';
import AssetsStepContract from './steps/AssetsStepContract';
import VendorStepContract from './steps/VendorStepContract';
import AttributesStepContract from './steps/AttributesStepContract';
import { useNavigate } from 'react-router-dom';

const MODAL_NAME = 'contractAdd';
const DEFAULT_FORM = {};
const CONTRACT_MODULE_ID = process.env.REACT_APP_MODULE_ID_CONTRACT;

const DEFAULT_NOTIFICATION_FORM = { time: 30 };

const STATUSES = {
	complete: 'text-green-700 bg-green-50 ring-green-600/20',
	default: 'text-gray-600 bg-gray-50 ring-gray-500/10',
	pending: 'text-yellow-800 bg-yellow-50 ring-yellow-600/20',
};

const AddContractBetaModal = () => {
	const navigate = useNavigate();
	const [currentContractStep, setCurrentContractStep] = useState(0);
	const [form, setForm] = useState({});
	const [updatedForm, setUpdateForm] = useState({});
	const [pmSchedulesForm, setPMSchedulesForm] = useState([]);
	const [notificationForm, setNotificationForm] = useState(
		DEFAULT_NOTIFICATION_FORM,
	);
	const [customAttributeForm, setCustomAttributeForm] = useState({});
	const [acceptedFiles, setAcceptedFiles] = useState(null);

	const { open: manageAttributesSettings, setOpen: openManageAttributes } =
		useModal('manageAttributes');

	const [isAddCustomAttributesModalOpen, setAddCustomAttributesModal] =
		useState(false);

	const { open: options, setClose } = useModal(MODAL_NAME);
	const {
		open: isPDFModalOpen,
		setClose: onCloseUploadContractPdfModal,
		setOpen: onOpenUploadContractPdfModal,
	} = useModal('uploadContractPdfModal');

	const [currentStep, setStep] = useState(1);
	const { open: addCategoryOpen, setOpen: setAddCategoryOpen } =
		useModal('addCategoryModal');

	const {
		value: moduleFields,
		status: isLoadingModuleFields,
		execute: onRefreshModuleFields,
	} = useAsync(getModuleFieldsBeta, {
		defaultPayload: { id: CONTRACT_MODULE_ID, active: true },
	});

	const { execute: onSetFieldActive } = useAsync(updateModuleFieldActive, {
		immediate: false,
	});

	const {
		value: categories,
		execute: onRefreshCategories,
		status: isLoadingCategories,
	} = useAsync(getModuleCategories, {
		immediate: false,
	});

	const { execute: handleAddContract, status: addingContract } = useAsync(
		addContract,
		{ immediate: false },
	);

	const { value: teamMembers } = useAsync(getTeam);

	const prevManageAttributes = usePrevious(manageAttributesSettings);
	useEffect(() => {
		if (!manageAttributesSettings && prevManageAttributes) {
			onRefreshModuleFields(
				{ categoryId: form?.category?.value?.value },
				null,
				{ silent: true },
			);
		}
	}, [manageAttributesSettings]);

	const previousCategory = usePrevious(form?.category?.value?.value);
	useEffect(() => {
		if (
			form?.category?.value?.value &&
			previousCategory !== form?.category?.value?.value
		) {
			onRefreshModuleFields(
				{ categoryId: form?.category?.value?.value },
				null,
				{ silent: true },
			);
		}
	}, [form?.category?.value?.value]);

	const prevOptions = usePrevious(options);
	useEffect(() => {
		if (options && !prevOptions) {
			onRefreshCategories({
				id: CONTRACT_MODULE_ID,
			});

			if (options?.assetId) {
				setUpdateForm({
					...updatedForm,
					assets: { value: [{ value: options?.assetId }] },
				});
			}
		}

		if (!options && prevOptions) {
			setForm({});
			setUpdateForm({});
			setStep(1);
			setCurrentContractStep(0);
			setPMSchedulesForm([]);
			setNotificationForm(DEFAULT_NOTIFICATION_FORM);
		}
	}, [options]);

	const previousCoverageStart = usePrevious(
		updatedForm['coverage-start']?.value,
	);
	useEffect(() => {
		if (previousCoverageStart !== updatedForm['coverage-start']) {
			setUpdateForm({
				...updatedForm,
				'coverage-end': {
					...(updatedForm['coverage-end'] || {
						fieldId: moduleFields?.combined?.find(
							(o) => o.key === 'coverage-end' && o.isDefault,
						)?.id,
					}),
					value: moment(updatedForm['coverage-start']?.value)
						.add(1, 'year')
						.subtract(1, 'day')
						.toDate(),
				},
			});
		}
	}, [updatedForm['coverage-start']?.value]);

	const SERVICE_CONTRACT_CATEGORY = categories?.find(
		(category) =>
			category?.key === 'default:servicecontract' && category.isDefault,
	);

	const WARRANTY_CONTRACT_CATEGORY = categories?.find(
		(category) => category?.key === 'default:warranty' && category.isDefault,
	);

	const CATEGORY_FIELD_ID = moduleFields?.combined?.find(
		(o) => o.key === 'category' && o.isDefault,
	)?.id;

	const id = null;
	const skipModuleKeys = [
		...(id ? ['coverage-start', 'duration'] : ['category']),
		...(options?.assetId ? ['assets'] : []),
	];

	const hasAllRequired = useRequiredBeta(
		{ ...form, ...updatedForm },
		moduleFields?.combined
			?.filter((field) => field.isRequired)
			.map((field) => field.key),
	);

	const onAddContract = ({ files = [] }) => {
		console.log(
			'onAddContract - pmSchedulesForm:',
			JSON.stringify(pmSchedulesForm, null, 2),
		);

		// Prepare PM schedules for backend
		const pmSchedules = pmSchedulesForm.map((schedule) => ({
			name: schedule.name,
			description: schedule.description || '',
			categoryId: schedule.categoryId,
			occurrences: schedule.occurrences,
			customDates:
				schedule.occurrences === 'custom' ? schedule.customDates : [],
			startDate: schedule.startDate || null,
			generateDaysBefore: parseInt(schedule.generateDaysBefore) || 30,
			sopId: schedule.sopId,
			serviceReportRequired: schedule.serviceReportRequired,
		}));

		const contractObject = {
			...configurePayload({ ...updatedForm, ...form }, moduleFields?.combined),
			...(form?.document ? { document: form?.document?.value } : {}),
			...{ pmSchedules, notification: notificationForm },
		};

		handleAddContract(
			{
				...(files?.length
					? {
							files: acceptedFiles,
							bucket: 'local-contracts',
							information: contractObject,
						}
					: contractObject),
			},
			(response) => {
				setAcceptedFiles(null);
				onCloseUploadContractPdfModal();
				setClose();
				if (typeof options?.onSuccess === 'function') {
					options.onSuccess(response);
				} else {
					navigate(`/manage/contracts/${response?.id}`);
				}
			},
			{
				showNotification: {
					success: 'Contract added',
					error: 'Something went wrong...',
				},
			},
		);
	};

	const steps = [
		{ name: 'General' },
		{ name: 'Attributes' },
		{ name: 'Assets' },
		{ name: 'PM Schedules' },
		{ name: 'Vendor' },
	];

	const moduleFieldsBreakdown = {
		general: [
			...(moduleFields?.standard?.filter(
				(field) =>
					field?.isDefault &&
					['name', 'coverage-start', 'coverage-end'].includes(field?.key),
			) || []),
		],
		assets: moduleFields?.standard?.filter(
			(field) => field?.isDefault && ['assets'].includes(field?.key),
		),
		vendor: moduleFields?.standard?.filter(
			(field) => field?.isDefault && ['vendor'].includes(field?.key),
		),
		customAttributes:
			moduleFields?.combined?.filter((field) => !field?.isDefault) || [],
	};

	return (
		<>
			<Modal
				title={
					currentStep === 1
						? 'Select contract type'
						: currentStep === 2
							? 'Upload PDF (optional)'
							: currentStep === 3
								? ''
								: ''
				}
				open={!!options}
				onClose={() => {
					setClose({ onClose: options?.onClose });
				}}
				size={currentStep === 3 ? '4xl' : undefined}
				preventBackdropClose
			>
				{/* 
				
					Step 1: Category Selection 
					
				*/}
				{currentStep === 1 && (
					<div className="grid grid-cols-2 gap-x-4 gap-y-4">
						<div
							onClick={() => {
								setForm({
									...form,
									category: {
										fieldId: CATEGORY_FIELD_ID,
										value: {
											label: SERVICE_CONTRACT_CATEGORY?.name,
											value: SERVICE_CONTRACT_CATEGORY?.id,
										},
									},
								});
								setStep(2);
							}}
							className="border border-gray-200 hover:border-gray-300 cursor-pointer rounded-lg text-xs h-36 flex justify-center items-center font-light text-gray-600 hover:text-gray-900 flex-col"
						>
							<DocumentCheckIcon className="w-6 h-6 text-gray-600 hover:text-gray-900 mb-3" />
							Service Contract
						</div>
						<div
							onClick={() => {
								setForm({
									...form,
									category: {
										fieldId: CATEGORY_FIELD_ID,
										value: {
											label: WARRANTY_CONTRACT_CATEGORY?.name,
											value: WARRANTY_CONTRACT_CATEGORY?.id,
										},
									},
								});
								setStep(2);
							}}
							className="border border-gray-200 hover:border-gray-300 cursor-pointer rounded-lg text-xs h-36 flex justify-center items-center font-light text-gray-600 hover:text-gray-900 flex-col"
						>
							<WrenchScrewdriverIcon className="w-6 h-6 text-gray-600 hover:text-gray-900 mb-3" />
							Warranty
						</div>
						<div className="col-span-2">
							<div className="flex">
								<div className="flex-1">
									<TemplateReactSelect
										name={'or choose a custom contract type'}
										placeholder="Select a custom contract type"
										dataIndex={'categoryId'}
										options={
											categories
												?.filter((category) => !category?.isDefault)
												.map((category) => ({
													label: category?.name,
													value: category?.id,
												})) || []
										}
										value={form['categoryId'] || ''}
										onChange={(index, category) => {
											setForm({
												...form,
												category: {
													fieldId: CATEGORY_FIELD_ID,
													value: category,
												},
											});
											setStep(2);
										}}
										disabled={isLoadingCategories === 'pending'}
									/>
								</div>
								{form['categoryId']?.value ? (
									<div className="flex-shrink flex items-center pt-7 ml-3">
										<XCircleIcon
											className="w-6 h-6 cursor-pointer text-gray-500"
											onClick={() => {
												setForm({ ...form, category: null });
											}}
										/>
									</div>
								) : (
									<div className="flex-shrink flex items-center pt-7 ml-3">
										<PlusCircleIcon
											className="w-6 h-6 cursor-pointer text-gray-500"
											onClick={() =>
												setAddCategoryOpen({
													moduleId: CONTRACT_MODULE_ID,
													onSubmit: (category) => {
														setForm({
															...form,
															category: {
																fieldId: CATEGORY_FIELD_ID,
																value: category,
															},
														});
														setStep(2);
													},
												})
											}
										/>
									</div>
								)}
							</div>
						</div>
					</div>
				)}

				{/* 
				
					Step 2: Document Selection 
					
				*/}
				{currentStep === 2 && (
					<div>
						<p className="text-xs text-gray-500 mb-4">
							If you would like to store the contract PDF provided by the
							vendor, feel free to upload it now. This is optional
						</p>
						<FileUpload
							files={acceptedFiles || []}
							onChange={(files) => {
								if (files.length > 0) {
									const file = files[0];
									if (file.fromLibrary) {
										// Selected from library - open PDF modal with URL
										setAcceptedFiles([file]);
										setForm({
											...form,
											document: { label: file.name, value: file.id },
										});
										onOpenUploadContractPdfModal(true);
									} else {
										// Uploaded file - open PDF modal with File object
										setForm({ ...form, document: null });
										setAcceptedFiles([file]);
										onOpenUploadContractPdfModal(true);
									}
								} else {
									setAcceptedFiles(null);
									setForm({ ...form, document: null });
								}
							}}
							multiple={false}
							showLibraryOption={true}
							libraryFileTypes={['application/pdf']}
							acceptTypes="application/pdf"
							buttonText="Add Contract Document"
							uploadText="Upload PDF"
							libraryText="Choose from Library"
							className="mt-4"
						/>
						<div className="flex justify-end mt-4">
							<Button
								type={
									acceptedFiles?.length || form?.document
										? 'primary'
										: 'secondary'
								}
								onClick={() => {
									if (acceptedFiles?.length) {
										onOpenUploadContractPdfModal(true);
									} else {
										setStep(3);
									}
								}}
							>
								{acceptedFiles?.length || form?.document ? 'Continue' : 'Skip'}
							</Button>
						</div>

						{/* 
				
							Step 2 1/2: PDF Modal Contract Details
					
						*/}

						<PDFModal
							setClose={() => {
								onCloseUploadContractPdfModal();
								setAcceptedFiles(null);
								setForm({ ...form, document: null });
							}}
							options={{
								file: acceptedFiles?.length
									? acceptedFiles[0].fromLibrary
										? acceptedFiles[0].location
										: acceptedFiles[0].previewUrl
									: null,
								actions: [
									{
										label: 'Add Contract',
										disabled: !hasAllRequired,
										onClick: () =>
											onAddContract({
												files: acceptedFiles?.[0]?.fromLibrary
													? []
													: [acceptedFiles?.[0]?.original],
											}),
									},
								],
							}}
						>
							<div className="grid gap-y-4 pt-3">
								<>
									{isLoadingModuleFields === 'pending' ? (
										<LoadingSkeleton />
									) : (
										<>
											<div
												className={classNames(
													'grid gap-y-4 gap-x-4 text-left grid-cols-2',
												)}
											>
												<div className="border-gray-300 bg-gray-100 upper text-xxs w-full px-4 py-3 rounded font-semibold col-span-2 text-gray-60">
													General Information
												</div>
												<GeneralStepContract
													moduleFieldsBreakdown={moduleFieldsBreakdown}
													skipModuleKeys={skipModuleKeys}
													form={form}
													setForm={setForm}
													updatedForm={updatedForm}
													setUpdateForm={setUpdateForm}
												/>

												{/*moduleFieldsBreakdown?.general
															?.filter(
																(field) =>
																	skipModuleKeys.indexOf(field.key) === -1
															)
															?.map((field) => (
																<>
																	<Attributes
																		field={field}
																		onChange={(index, value, fullValue) => {
																			if (value || fullValue) {
																				let updatedValue = form[index]?.value;

																				if (fullValue) {
																					updatedValue = fullValue;
																				} else {
																					if (
																						updatedValue &&
																						Array.isArray(value)
																					) {
																						updatedValue = [
																							...(Array.isArray(updatedValue)
																								? updatedValue
																								: [updatedValue]),
																							...value,
																						];
																					} else {
																						updatedValue = value;
																					}
																				}

																				setUpdateForm({
																					...updatedForm,
																					[index]: {
																						value: updatedValue,
																						fieldId: field.id,
																					},
																				});
																			} else {
																				let updated = { ...updatedForm };
																				updated[index] = {
																					...updated[index],
																					value: null,
																				};

																				setUpdateForm(updated);
																			}
																		}}
																		form={{ ...form, ...updatedForm }}
																		updatedForm={updatedForm}
																		setForm={setUpdateForm}
																		moduleId={CONTRACT_MODULE_ID}
																	/>
																</>
															))*/}

												{/* 
				
														Step 2.12: Main Content - Attributes
														
													*/}

												<div className="border-gray-300 bg-gray-100 upper text-xxs w-full px-4 py-3 rounded font-semibold col-span-2 text-gray-60">
													Attributes
												</div>

												<AttributesStepContract
													moduleFieldsBreakdown={moduleFieldsBreakdown}
													skipModuleKeys={skipModuleKeys}
													form={form}
													setForm={setForm}
													updatedForm={updatedForm}
													setUpdateForm={setUpdateForm}
												/>

												<div className="border-gray-300 bg-gray-100 upper text-xxs w-full px-4 py-3 rounded font-semibold col-span-2 text-gray-60">
													Assets
												</div>

												<AssetsStepContract
													moduleFieldsBreakdown={moduleFieldsBreakdown}
													skipModuleKeys={skipModuleKeys}
													form={form}
													setForm={setForm}
													updatedForm={updatedForm}
													setUpdateForm={setUpdateForm}
												/>

												<div className="border-gray-300 bg-gray-100 upper text-xxs w-full px-4 py-3 rounded font-semibold col-span-2 text-gray-60">
													PM Schedule
												</div>
												<ContractPMSchedules
													pmSchedulesForm={pmSchedulesForm}
													setPMSchedulesForm={setPMSchedulesForm}
													updatedForm={updatedForm}
												/>

												<div className="border-gray-300 bg-gray-100 upper text-xxs w-full px-4 py-3 rounded font-semibold col-span-2 text-gray-60">
													Vendors
												</div>
												<VendorStepContract
													moduleFieldsBreakdown={moduleFieldsBreakdown}
													skipModuleKeys={skipModuleKeys}
													form={form}
													setForm={setForm}
													updatedForm={updatedForm}
													setUpdateForm={setUpdateForm}
												/>
											</div>
										</>
									)}
								</>

								<nav
									aria-label="Progress"
									className="flex items-center justify-center mt-3"
								>
									<p className="text-sm font-medium">
										{`${currentContractStep + 1} `}
										of {steps.length}
									</p>
									<ol role="list" className="ml-8 flex items-center space-x-5">
										{steps.map((step, stepIdx) => (
											<li key={step.name}>
												{stepIdx < currentContractStep ? (
													<a
														href={step.href}
														className="block h-2.5 w-2.5 rounded-full bg-indigo-600 hover:bg-indigo-900"
													>
														<span className="sr-only">{step.name}</span>
													</a>
												) : stepIdx === currentContractStep ? (
													<a
														href={step.href}
														aria-current="step"
														className="relative flex items-center justify-center"
													>
														<span
															aria-hidden="true"
															className="absolute flex h-5 w-5 p-px"
														>
															<span className="h-full w-full rounded-full bg-indigo-200" />
														</span>
														<span
															aria-hidden="true"
															className="relative block h-2.5 w-2.5 rounded-full bg-indigo-600"
														/>
														<span className="sr-only">{step.name}</span>
													</a>
												) : (
													<a
														href={step.href}
														className="block h-2.5 w-2.5 rounded-full bg-gray-200 hover:bg-gray-400"
													>
														<span className="sr-only">{step.name}</span>
													</a>
												)}
											</li>
										))}
									</ol>
								</nav>

								<div className="flex justify-between mt-4">
									<Button
										type={currentContractStep === 0 ? 'secondary' : undefined}
										disabled={currentContractStep === 0}
										onClick={() =>
											setCurrentContractStep(currentContractStep - 1)
										}
									>
										Previous
									</Button>

									{currentContractStep + 1 !== steps?.length && (
										<Button
											onClick={() => {
												setCurrentContractStep(currentContractStep + 1);
											}}
											type="ghost"
										>
											{`Optional: Add ${steps[currentContractStep + 1]?.name}`}
										</Button>
									)}
								</div>
							</div>
						</PDFModal>
					</div>
				)}

				{/* 
				
					Step 3: Contract Details (Non-modal)
					
				*/}
				{currentStep === 3 && (
					<div className="grid grid-cols-12 gap-x-4">
						{/* 
				
							Step 3.1: Sidebar
							
						*/}
						<div className="col-span-2 my-5">
							<nav aria-label="Progress" className="flex">
								<ol role="list" className="space-y-6">
									{steps.map((step, stepIdx) => (
										<li key={step.name}>
											{stepIdx < currentContractStep ? (
												<a
													href={step.href}
													className="group cursor-pointer"
													onClick={() => setCurrentContractStep(stepIdx)}
												>
													<div className="flex items-center">
														<div
															aria-hidden="true"
															className="relative flex h-5 w-5 flex-shrink-0 items-center justify-center"
														>
															<div className="h-2 w-2 rounded-full bg-gray-300 group-hover:bg-gray-400" />
														</div>
														<p className="ml-3 text-xs font-medium text-gray-500 group-hover:text-gray-900">
															{step.name}
														</p>
													</div>
												</a>
											) : stepIdx === currentContractStep ? (
												<a
													href={step.href}
													aria-current="step"
													className="flex items-center cursor-pointer"
													onClick={() => setCurrentContractStep(stepIdx)}
												>
													<span
														aria-hidden="true"
														className="relative flex h-5 w-5 flex-shrink-0 items-center justify-center"
													>
														<span className="absolute h-4 w-4 rounded-full bg-indigo-200" />
														<span className="relative block h-2 w-2 rounded-full bg-indigo-600" />
													</span>
													<span className="ml-3 text-xs font-medium text-indigo-600">
														{step.name}
													</span>
												</a>
											) : (
												<a
													href={step.href}
													className="group cursor-pointer"
													onClick={() => setCurrentContractStep(stepIdx)}
												>
													<div className="flex items-center">
														<div
															aria-hidden="true"
															className="relative flex h-5 w-5 flex-shrink-0 items-center justify-center"
														>
															<div className="h-2 w-2 rounded-full bg-gray-300 group-hover:bg-gray-400" />
														</div>
														<p className="ml-3 text-xs font-medium text-gray-500 group-hover:text-gray-900">
															{step.name}
														</p>
													</div>
												</a>
											)}
										</li>
									))}
								</ol>
							</nav>
						</div>

						{/* 
				
							Step 3.1: Main Content
							
						*/}
						<div className="col-span-10 pl-4">
							<div className="grid gap-y-4 pt-3">
								<>
									{isLoadingModuleFields === 'pending' ? (
										<LoadingSkeleton />
									) : (
										<>
											<div className="text-lg flex items-center justify-between">
												{steps[currentContractStep]?.name}
											</div>
											<div className=" min-h-72">
												<div
													className={classNames(
														'grid gap-y-4 gap-x-4 text-left',
														currentContractStep === 4 ||
															currentContractStep === 3
															? 'grid-cols-1'
															: 'grid-cols-2',
													)}
												>
													{/* 
				
														Step 3.11: Main Content - General Details
														
													*/}
													{currentContractStep === 0 && (
														<GeneralStepContract
															moduleFieldsBreakdown={moduleFieldsBreakdown}
															skipModuleKeys={skipModuleKeys}
															form={form}
															setForm={setForm}
															updatedForm={updatedForm}
															setUpdateForm={setUpdateForm}
														/>
													)}

													{/* 
				
														Step 3.12: Main Content - Attributes
														
													*/}
													{currentContractStep === 1 && (
														<>
															<AttributesStepContract
																moduleFieldsBreakdown={moduleFieldsBreakdown}
																skipModuleKeys={skipModuleKeys}
																form={form}
																setForm={setForm}
																updatedForm={updatedForm}
																setUpdateForm={setUpdateForm}
															/>
														</>
													)}

													{/* 
				
														Step 3.13: Main Content - Assets
														
													*/}

													{currentContractStep === 2 && (
														<AssetsStepContract
															moduleFieldsBreakdown={moduleFieldsBreakdown}
															skipModuleKeys={skipModuleKeys}
															form={form}
															setForm={setForm}
															updatedForm={updatedForm}
															setUpdateForm={setUpdateForm}
														/>
													)}

													{/*

														Step 3.14: Main Content - PM Schedules

													*/}
													{currentContractStep === 3 && (
														<>
															<div className="col-span-2 space-y-4">
																<ContractPMSchedules
																	pmSchedulesForm={pmSchedulesForm}
																	setPMSchedulesForm={setPMSchedulesForm}
																	updatedForm={updatedForm}
																/>
															</div>
														</>
													)}

													{/* 
				
														Step 3.15: Main Content - Vendor Details
														
													*/}
													{currentContractStep === 4 && (
														<VendorStepContract
															moduleFieldsBreakdown={moduleFieldsBreakdown}
															skipModuleKeys={skipModuleKeys}
															form={form}
															setForm={setForm}
															updatedForm={updatedForm}
															setUpdateForm={setUpdateForm}
														/>
													)}
												</div>
											</div>
										</>
									)}
								</>

								<div className="flex justify-end mt-4">
									{currentContractStep + 1 !== steps?.length && (
										<Button
											onClick={() => {
												setCurrentContractStep(currentContractStep + 1);
											}}
											type="ghost"
										>
											{`Optional: Add ${steps[currentContractStep + 1]?.name}`}
										</Button>
									)}

									<Button
										disabled={!hasAllRequired}
										onClick={() => onAddContract({ files: acceptedFiles })}
									>
										Add Contract
									</Button>
								</div>
							</div>
						</div>
					</div>
				)}
			</Modal>

			<Modal
				title="Add custom field"
				open={isAddCustomAttributesModalOpen}
				onClose={() => setAddCustomAttributesModal(false)}
				size="xl"
			>
				<>
					{isLoadingModuleFields === 'pending' ? (
						<LoadingSkeleton />
					) : (
						<>
							<div className="grid grid-cols-1 gap-y-4 gap-x-4 text-left">
								<TemplateCreateable
									name="Attribute"
									placeholder="Select or type to create..."
									options={moduleFields?.standard
										?.filter((field) => !field?.isDefault)
										.map((field) => ({
											label: field?.label,
											value: field?.id,
										}))}
									onChange={(index, selectedCustomField, raw) => {
										if (raw) {
											setCustomAttributeForm({
												...customAttributeForm,
												...(raw?.__isNew__
													? { label: raw?.label }
													: { id: raw?.value }),
											});
										} else {
											setCustomAttributeForm({});
										}
									}}
									skip={[]}
									value={customAttributeForm?.label || customAttributeForm?.id}
								/>

								{customAttributeForm?.label ? (
									<div className="grid grid-cols-2 gap-y-4">
										<TemplateReactSelect
											name="Type"
											options={FIELD_OPTIONS}
											onChange={(index, type) =>
												setCustomAttributeForm({ ...customAttributeForm, type })
											}
											value={customAttributeForm?.type}
										/>
										<TemplateBoolean
											span={'sm:col-span-1'}
											name="Required"
											className="mr-3"
											onChange={(index, required) =>
												setCustomAttributeForm({
													...customAttributeForm,
													required,
												})
											}
											value={customAttributeForm?.required}
										/>
										<TemplateBoolean
											span={'sm:col-span-1'}
											name="Searchable"
											onChange={(index, searchable) =>
												setCustomAttributeForm({
													...customAttributeForm,
													searchable,
												})
											}
											value={customAttributeForm?.searchable}
										/>

										<TemplateField
											span={'sm:col-span-2'}
											name="Value"
											className="mr-3"
											onChange={(index, value) =>
												setCustomAttributeForm({
													...customAttributeForm,
													value,
												})
											}
											value={customAttributeForm?.value}
										/>
									</div>
								) : null}
							</div>

							<div className="col-span-2 flex justify-end mt-8">
								<Button onClick={() => setAddCustomAttributesModal(false)}>
									Done
								</Button>
							</div>
						</>
					)}
				</>
			</Modal>
		</>
	);
};

export default AddContractBetaModal;
